import React, { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../../../firebase/AuthContext";
import { getApiUrl } from "../../../../../config/api";
import ProductCard, { ComplianceBadge } from "./ProductCard";
import ProductDetailModal from "./ProductDetailModal";

function CategoryCombobox({ categories, value, onChange, language }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  // Close when clicking outside
  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // Flatten every row into a searchable option with its full breadcrumb path
  const options = useMemo(() => {
    const rows = categories.map((c) => {
      const cat = c.category_name || "";
      const sub = c.sub_category || "";
      const subsub = c.sub_sub_category || "";
      const parts = [cat, sub, subsub].filter(Boolean);
      return {
        id: c.category_id,
        depth: parts.length - 1,
        leaf: parts[parts.length - 1] || "",
        path: parts.join(" › "),
        search: parts.join(" ").toLowerCase(),
        cat,
        sub,
        subsub,
      };
    });
    // Sort by the raw fields so children always sit directly under their parent:
    // group by category name, then parent (empty sub/sub-sub) before its
    // descendants, then alphabetically within each level.
    const byLevel = (a, b, key) => {
      if (a[key] === b[key]) return 0;
      if (!a[key]) return -1; // parent (empty) comes first
      if (!b[key]) return 1;
      return a[key].localeCompare(b[key]);
    };
    return rows.sort(
      (a, b) =>
        a.cat.localeCompare(b.cat) ||
        byLevel(a, b, "sub") ||
        byLevel(a, b, "subsub"),
    );
  }, [categories]);

  const selected = options.find((o) => o.id === value) || null;

  const q = query.trim().toLowerCase();
  const filtered = q ? options.filter((o) => o.search.includes(q)) : options;

  const allLabel = language === "zh" ? "所有類別" : "All categories";

  const choose = (id) => {
    onChange?.(id ?? null);
    setQuery("");
    setOpen(false);
  };

  return (
    <div ref={wrapRef} className="relative w-full">
      <svg
        className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
        />
      </svg>
      <input
        ref={inputRef}
        type="text"
        value={open ? query : selected ? selected.path : ""}
        onChange={(e) => {
          setQuery(e.target.value);
          if (!open) setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        placeholder={selected ? selected.path : allLabel}
        className="w-full pl-9 pr-8 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-gray-700 text-sm placeholder-gray-400 focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-200 transition-all"
      />
      {selected && !open ? (
        <button
          onClick={() => choose(null)}
          className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
          title={language === "zh" ? "清除" : "Clear"}
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      ) : (
        <svg
          className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      )}

      {open && (
        <div className="absolute z-30 mt-1 w-full max-h-72 overflow-auto bg-white border border-gray-200 rounded-xl shadow-lg py-1">
          <button
            onClick={() => choose(null)}
            className={`w-full text-left px-3 py-2 text-sm transition-colors hover:bg-orange-50 ${
              value == null ? "text-orange-600 font-medium" : "text-gray-600"
            }`}
          >
            {allLabel}
          </button>
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-400">
              {language === "zh" ? "無匹配類別" : "No matches"}
            </div>
          ) : (
            filtered.map((o) => (
              <button
                key={o.id}
                onClick={() => choose(o.id)}
                style={{ paddingLeft: `${12 + o.depth * 16}px` }}
                className={`w-full text-left pr-3 py-2 text-sm flex items-center gap-1.5 transition-colors hover:bg-orange-50 ${
                  o.id === value
                    ? "bg-orange-50 text-orange-600"
                    : "text-gray-700"
                }`}
              >
                {o.depth > 0 && <span className="text-gray-300">└</span>}
                <span className={o.depth === 0 ? "font-semibold" : ""}>
                  {q ? o.path : o.leaf}
                </span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function DatabaseView({
  language,
  loading,
  products,
  pagination = { page: 1, per_page: 20, total: 0, pages: 1 },
  categories = [],
  categoryFilter,
  searchQuery = "",
  onCategoryChange,
  loadingSearch,
  onSearchChange,
  onPageChange,
  onProductDeleted,
  onNavigateToUpload,
}) {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [viewMode, setViewMode] = useState("grid"); // 'grid' or 'list'

  // Local input value — only submitted on Enter or Search button click
  const [searchInput, setSearchInput] = useState(searchQuery || "");

  const commitSearch = () => {
    onSearchChange?.(searchInput);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") commitSearch();
  };

  // ── View (Full Details) modal ─────────────────────────────────────────────
  const [showProductModal, setShowProductModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [loadingProductData, setLoadingProductData] = useState(false);

  // ── Delete modal ──────────────────────────────────────────────────────────
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [deletingProduct, setDeletingProduct] = useState(false);

  // ── Recently updated banner ───────────────────────────────────────────────
  const [recentProducts, setRecentProducts] = useState([]);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!currentUser) return;
    let cancelled = false;
    (async () => {
      try {
        const token = await currentUser.getIdToken();
        const res = await axios.get(
          `${getApiUrl()}/api/v2/products/recent?days=7`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!cancelled && res.data?.data?.length > 0) {
          setRecentProducts(res.data.data);
          setBannerDismissed(false);
        }
      } catch (err) {
        console.error("Failed to fetch recently updated products:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentUser]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleViewProductData = async (productModelName) => {
    try {
      setShowProductModal(true);
      setLoadingProductData(true);
      setSelectedProduct(null);

      const token = await currentUser.getIdToken();
      const existing = products.find((p) => p.model_id === productModelName);
      if (!existing) throw new Error("Product not found in local list.");

      const response = await axios.get(
        `${getApiUrl()}/api/v2/products/${existing.product_id}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (response.data?.data) setSelectedProduct(response.data.data);
    } catch (error) {
      console.error("Error fetching product data:", error);
      setShowProductModal(false);
      alert(
        language === "zh" ? "無法加載產品數據" : "Failed to load product data",
      );
    } finally {
      setLoadingProductData(false);
    }
  };

  /**
   * Open the upload panel in edit mode for this product's current version.
   * Only the IDs are handed over — ProductProperties loads the rest itself,
   * the same path it takes after a page reload.
   */
  const handleEditProduct = async (product) => {
    let versionId = product.current_version_id;

    // A product whose current version pointer was never set still has
    // snapshots; fall back to its newest one.
    if (!versionId) {
      try {
        const token = await currentUser.getIdToken();
        const response = await axios.get(
          `${getApiUrl()}/api/v2/products/${product.product_id}/versions`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const versions = response.data?.data || [];
        versionId = versions[versions.length - 1]?.version_id;
      } catch (error) {
        console.error("Error loading versions for edit:", error);
      }
    }

    if (!versionId) {
      alert(
        language === "zh"
          ? "此產品沒有可編輯的版本"
          : "This product has no version to edit",
      );
      return;
    }

    const search = new URLSearchParams({
      lang: language,
      mode: "edit",
      product_id: String(product.product_id),
      version_id: String(versionId),
    }).toString();
    navigate({ pathname: "/product-properties", search: `?${search}` });
  };

  const handleDeleteProduct = (productId) => {
    setProductToDelete(productId);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!productToDelete) return;
    setDeletingProduct(true);
    try {
      const token = await currentUser.getIdToken();
      await axios.delete(`${getApiUrl()}/api/v2/products/${productToDelete}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      alert(
        language === "zh" ? "產品已成功刪除" : "Product deleted successfully",
      );
      setShowDeleteModal(false);
      setProductToDelete(null);
      onProductDeleted?.();
    } catch (error) {
      console.error("Error deleting product:", error);
      let errorMessage = "Failed to delete product";
      if (error.response?.status === 404) {
        errorMessage = "Product not found. It may have been already deleted.";
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }
      alert(
        language === "zh"
          ? `刪除失敗: ${errorMessage}`
          : `Delete failed: ${errorMessage}`,
      );
    } finally {
      setDeletingProduct(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setProductToDelete(null);
  };

  return (
    <div className="space-y-6 relative">
      {/* ── Recently Updated Banner ──────────────────────────────── */}
      {recentProducts.length > 0 && !bannerDismissed && (
        <div className="relative overflow-hidden rounded-xl border border-orange-400 shadow-lg shadow-orange-500/20 bg-gradient-to-r from-orange-500 to-orange-400">
          {/* Subtle background pattern */}
          <div
            className="absolute inset-0 opacity-10"
            style={{
              backgroundImage:
                "radial-gradient(circle at 20% 50%, white 1px, transparent 1px), radial-gradient(circle at 80% 50%, white 1px, transparent 1px)",
              backgroundSize: "30px 30px",
            }}
          />

          <div className="relative flex items-center gap-4 px-5 py-4">
            {/* Animated bell icon */}
            <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center ring-2 ring-white/30">
              <svg
                className="w-5 h-5 text-white "
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6 6 0 10-12 0v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                />
              </svg>
            </div>

            {/* Text block */}
            <div className="flex-shrink-0">
              <p className="text-white font-bold text-sm leading-tight">
                {language === "zh" ? "近期產品更新" : "Recent Product Updates"}
              </p>
              <p className="text-orange-100 text-xs mt-0.5">
                {recentProducts.length}
                {language === "zh"
                  ? " 個產品在過去 7 天內更新"
                  : ` product${recentProducts.length > 1 ? "s" : ""} updated in the last 7 days`}
              </p>
            </div>

            {/* Divider */}
            <div className="flex-shrink-0 w-px h-10 bg-white/30 hidden sm:block" />

            {/* Scrollable pill list */}
            <div
              ref={scrollRef}
              className="flex gap-2 overflow-x-auto no-scrollbar flex-1 py-0.5"
            >
              {recentProducts.map((p) => (
                <span
                  key={p.version_id}
                  className="flex-shrink-0 inline-flex items-center gap-2 px-3 py-1.5 bg-white/20 hover:bg-white/30 border border-white/30 rounded-lg text-xs font-semibold text-white transition-colors cursor-default"
                  title={`${language === "zh" ? "版本" : "Version"} ${p.version_number} — ${new Date(p.created_at).toLocaleDateString()}`}
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-white flex-shrink-0 animate-pulse" />
                  {p.model_id || `#${p.product_id}`}
                  <span className="px-1.5 py-0.5 bg-white/20 rounded text-[10px] font-bold">
                    v{p.version_number}
                  </span>
                </span>
              ))}
            </div>

            {/* Date range label */}
            <span className="flex-shrink-0 text-xs text-orange-100 whitespace-nowrap hidden lg:block">
              {new Date(Date.now() - 7 * 86400000).toLocaleDateString()} –{" "}
              {new Date().toLocaleDateString()}
            </span>

            {/* Dismiss */}
            <button
              onClick={() => setBannerDismissed(true)}
              className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-lg bg-white/10 hover:bg-white/25 text-white transition-colors"
              title={language === "zh" ? "關閉" : "Dismiss"}
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2.5}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* ── Modals ───────────────────────────────────────────────────── */}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => !deletingProduct && cancelDelete()}
          />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            {/* Header */}
            <div className="flex items-start justify-between px-6 pt-6 pb-4 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center">
                  <svg
                    className="w-4 h-4 text-red-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">
                    {language === "zh" ? "確認刪除" : "Delete Product"}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {language === "zh"
                      ? "此操作無法撤銷"
                      : "This action cannot be undone"}
                  </p>
                </div>
              </div>
              <button
                onClick={cancelDelete}
                disabled={deletingProduct}
                className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg p-1.5 transition-all disabled:opacity-50"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              <p className="text-sm text-gray-700 mb-4">
                {language === "zh" ? (
                  <>
                    您即將刪除{" "}
                    <span className="font-bold text-gray-900">
                      {products.find((p) => p.product_id === productToDelete)
                        ?.model_id || "this product"}
                    </span>
                  </>
                ) : (
                  <>
                    You are about to delete{" "}
                    <span className="font-bold text-gray-900">
                      {products.find((p) => p.product_id === productToDelete)
                        ?.model_id || "this product"}
                    </span>
                  </>
                )}
              </p>

              <div className="flex items-start gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-xl">
                <svg
                  className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
                <p className="text-xs text-red-700">
                  {language === "zh"
                    ? "此操作無法撤銷，所有相關數據將被永久刪除"
                    : "This action cannot be undone. All related data will be permanently removed."}
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50/50">
              <button
                onClick={cancelDelete}
                disabled={deletingProduct}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-all disabled:opacity-50"
              >
                {language === "zh" ? "取消" : "Cancel"}
              </button>
              <button
                onClick={confirmDelete}
                disabled={deletingProduct}
                className="px-5 py-2 bg-red-500 hover:bg-red-600 text-white rounded-xl text-sm font-medium transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deletingProduct ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin"></div>
                    {language === "zh" ? "刪除中..." : "Deleting..."}
                  </div>
                ) : language === "zh" ? (
                  "確認刪除"
                ) : (
                  "Delete"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Product Detail Modal */}
      <ProductDetailModal
        show={showProductModal}
        onClose={() => setShowProductModal(false)}
        selectedProduct={selectedProduct}
        loadingProductData={loadingProductData}
        language={language}
        currentUser={currentUser}
      />

      <button
        onClick={onNavigateToUpload}
        className="fixed bottom-8 right-8 z-50 flex items-center gap-2 px-6 py-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl shadow-2xl shadow-orange-500/50 hover:shadow-orange-500/70 hover:scale-110 transition-all duration-300 group"
        title={language === "zh" ? "新增產品" : "Add Product"}
      >
        <svg
          className="w-5 h-5 group-hover:rotate-90 transition-transform duration-300"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2.5}
            d="M12 4v16m8-8H4"
          />
        </svg>
        <span className="font-semibold">
          {language === "zh" ? "新增產品" : "Add Product"}
        </span>
      </button>

      {/* Header Section */}
      <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
          <div className="flex items-start gap-4 flex-1">
            <div className="flex-1 min-w-0">
              <h2 className="text-xl font-bold text-gray-900 mb-2">
                {language === "zh" ? "產品數據庫" : "Product Database"}
              </h2>
              <p className="text-gray-600 text-sm">
                {pagination.total} {language === "zh" ? "個產品" : "products"}
                {(searchQuery || categoryFilter) &&
                  ` ${language === "zh" ? "符合搜索條件" : "found"}`}
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full lg:w-auto">
            {/* Model ID search */}
            <div className="flex items-center gap-2 flex-1 lg:w-auto">
              <div className="flex flex-col gap-2 w-full">
                <div className="flex flex-row gap-2 items-center w-full">
                  <div className="relative flex-1 lg:w-56">
                    <svg
                      className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                    <input
                      type="text"
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                      onKeyDown={handleSearchKeyDown}
                      placeholder={
                        language === "zh"
                          ? "搜索型號或描述..."
                          : "Search model ID or description..."
                      }
                      className="w-full pl-10 pr-8 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-gray-800 placeholder-gray-400 focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-200 transition-all text-sm"
                    />
                    {searchInput && (
                      <button
                        onClick={() => {
                          setSearchInput("");
                          onSearchChange?.("");
                        }}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                        title={language === "zh" ? "清除" : "Clear"}
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    )}
                  </div>
                  <button
                    onClick={commitSearch}
                    className="px-4 py-2.5 bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium rounded-xl transition-all shadow-sm flex-shrink-0"
                  >
                    {language === "zh" ? "搜索" : "Search"}
                  </button>
                </div>

                <CategoryCombobox
                  categories={categories}
                  value={categoryFilter ?? null}
                  onChange={onCategoryChange}
                  language={language}
                />
              </div>
            </div>

            {/* View Toggle */}
            <div className="flex items-center bg-gray-300 rounded-xl p-1 border border-gray-300">
              <button
                onClick={() => setViewMode("grid")}
                className={`p-2 rounded-xl transition-all ${viewMode === "grid" ? "bg-orange-500 text-white" : "text-gray-400 hover:text-orange-500"}`}
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
                  />
                </svg>
              </button>
              <button
                onClick={() => setViewMode("list")}
                className={`p-2 rounded-xl transition-all ${viewMode === "list" ? "bg-orange-500 text-white" : "text-gray-400 hover:text-orange-500"}`}
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 10h16M4 14h16M4 18h16"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      {pagination.pages > 1 && !loadingSearch && (
        <div className="flex items-center justify-between bg-white rounded-xl px-6 py-4 border border-gray-200 shadow-sm">
          <p className="text-sm text-gray-500">
            {language === "zh"
              ? `第 ${pagination.page} 頁，共 ${pagination.pages} 頁 (${pagination.total} 個產品)`
              : `Page ${pagination.page} of ${pagination.pages} (${pagination.total} products)`}
          </p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => onPageChange?.(pagination.page - 1)}
              disabled={pagination.page <= 1 || loading}
              className="p-2 rounded-lg text-gray-500 hover:bg-orange-50 hover:text-orange-500 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>

            {Array.from({ length: Math.min(pagination.pages, 7) }, (_, i) => {
              // Show pages around the current page
              let p;
              if (pagination.pages <= 7) {
                p = i + 1;
              } else if (pagination.page <= 4) {
                p = i + 1;
              } else if (pagination.page >= pagination.pages - 3) {
                p = pagination.pages - 6 + i;
              } else {
                p = pagination.page - 3 + i;
              }
              return (
                <button
                  key={p}
                  onClick={() => onPageChange?.(p)}
                  disabled={loading}
                  className={`w-8 h-8 rounded-lg text-sm font-medium transition-all ${
                    p === pagination.page
                      ? "bg-orange-500 text-white shadow-sm"
                      : "text-gray-600 hover:bg-orange-50 hover:text-orange-500"
                  }`}
                >
                  {p}
                </button>
              );
            })}

            <button
              onClick={() => onPageChange?.(pagination.page + 1)}
              disabled={pagination.page >= pagination.pages || loading}
              className="p-2 rounded-lg text-gray-500 hover:bg-orange-50 hover:text-orange-500 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Product Grid/List */}
      {loadingSearch ? (
        viewMode === "grid" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="bg-white border border-gray-200 rounded-xl p-5 animate-pulse"
              >
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-3" />
                <div className="h-3 bg-gray-100 rounded w-1/2 mb-4" />
                <div className="h-3 bg-gray-100 rounded w-full mb-2" />
                <div className="h-3 bg-gray-100 rounded w-5/6" />
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="bg-white border border-gray-200 rounded-xl p-5 animate-pulse"
              >
                <div className="flex items-center gap-4">
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-1/3" />
                    <div className="h-3 bg-gray-100 rounded w-1/4" />
                  </div>
                  <div className="h-8 w-20 bg-gray-200 rounded-xl" />
                </div>
              </div>
            ))}
          </div>
        )
      ) : products.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 bg-gray-800/30 rounded-xl border border-gray-700/50">
          <div className="w-20 h-20 bg-gray-700/50 rounded-xl flex items-center justify-center mb-4">
            <svg
              className="w-10 h-10 text-gray-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
              />
            </svg>
          </div>
          <p className="text-white text-lg font-medium mb-1">
            {searchQuery || categoryFilter
              ? language === "zh"
                ? "未找到匹配的產品"
                : "No matching products found"
              : language === "zh"
                ? "暫無產品"
                : "No products yet"}
          </p>
          <p className="text-gray-400 text-sm">
            {searchQuery || categoryFilter
              ? language === "zh"
                ? "嘗試使用不同的搜索詞"
                : "Try a different search term"
              : language === "zh"
                ? "上傳新產品以開始"
                : "Upload a new product to get started"}
          </p>
        </div>
      ) : viewMode === "grid" ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {products.map((product, index) => (
            <ProductCard
              key={product.product_id || product.id}
              product={product}
              index={index}
              language={language}
              onView={handleViewProductData}
              onEdit={handleEditProduct}
              onDelete={handleDeleteProduct}
            />
          ))}
        </div>
      ) : (
        <div className="space-y-3">
          {products.map((product, index) => (
            <div
              key={product.product_id || product.id}
              className="group bg-white border border-gray-200 rounded-xl p-5 hover:border-orange-500/50 transition-all duration-300 hover:shadow-lg"
              style={{ animationDelay: `${index * 30}ms` }}
            >
              <div className="flex items-start justify-between gap-6">
                {/* Left Section - Product Info */}
                <div className="flex items-start gap-4 flex-1 min-w-0">
                  {/* <div className="w-12 h-12 bg-orange-500/10 rounded-xl flex items-center justify-center flex-shrink-0">
                                        <svg
                                            className="w-6 h-6 text-orange-500"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                                            />
                                        </svg>
                                    </div> */}
                  <div className="min-w-0 flex-1 space-y-2">
                    <div>
                      <h3 className="text-gray-900 font-bold text-lg truncate group-hover:text-orange-500 transition-colors uppercase">
                        {product.model_id || product.name}
                      </h3>
                      <p className="text-gray-500 text-sm capitalize">
                        {product.category}
                      </p>
                    </div>

                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
                      <div className="flex items-center gap-1.5">
                        <svg
                          className="w-3.5 h-3.5 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                          />
                        </svg>
                        <span className="text-gray-700 font-medium capitalize">
                          {[
                            product.category_name,
                            product.subcategory_name,
                            product.subsubcategory_name,
                          ]
                            .filter(Boolean)
                            .join(" › ") ||
                            (language === "zh" ? "未分類" : "Uncategorized")}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <svg
                          className="w-3.5 h-3.5 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                          />
                        </svg>
                        <span className="text-gray-600 font-mono">
                          #{product.product_id}
                        </span>
                      </div>
                      {product.pdf_url && (
                        <a
                          href={product.pdf_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1.5 text-orange-500 hover:text-orange-600 hover:underline"
                        >
                          <svg
                            className="w-3.5 h-3.5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                            />
                          </svg>
                          {language === "zh" ? "PDF" : "PDF"}
                        </a>
                      )}
                    </div>
                  </div>
                </div>

                {/* Middle Section - Compliance Badges */}
                <div className="hidden lg:flex items-center gap-2 flex-shrink-0">
                  {product.rohs_compliant && (
                    <ComplianceBadge
                      label="RoHS"
                      isCompliant={product.rohs_compliant}
                    />
                  )}
                  {product.reach_compliant && (
                    <ComplianceBadge
                      label="REACH"
                      isCompliant={product.reach_compliant}
                    />
                  )}
                  {product.ul_comparable && (
                    <ComplianceBadge
                      label="UL"
                      isCompliant={product.ul_comparable}
                    />
                  )}
                </div>

                {/* Right Section - Action Buttons */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={() => handleViewProductData(product.model_id)}
                    className="px-3 py-2 bg-orange-500 text-white rounded-xl hover:bg-orange-600 transition-all text-sm font-medium shadow-lg shadow-orange-500/30 flex items-center gap-1.5"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                    <span className="hidden xl:inline">
                      {language === "zh" ? "詳情" : "Details"}
                    </span>
                  </button>
                  {/* <button
                                        onClick={() =>
                                            handleEditProduct(product)
                                        }
                                        className="p-2 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-all shadow-lg shadow-blue-500/30"
                                        title={
                                            language === "zh"
                                                ? "編輯產品"
                                                : "Edit Product"
                                        }
                                    >
                                        <svg
                                            className="w-4 h-4"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                            />
                                        </svg>
                                    </button> */}
                  <button
                    onClick={() => handleDeleteProduct(product.product_id)}
                    className="p-2 bg-red-500 text-white rounded-xl hover:bg-red-600 transition-all shadow-lg shadow-red-500/30"
                    title={language === "zh" ? "刪除產品" : "Delete Product"}
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Mobile Compliance Badges */}
              <div className="flex lg:hidden items-center gap-2 mt-3 pt-3 border-t border-gray-200">
                {product.rohs_compliant && (
                  <ComplianceBadge
                    label="RoHS"
                    isCompliant={product.rohs_compliant}
                  />
                )}
                {product.reach_compliant && (
                  <ComplianceBadge
                    label="REACH"
                    isCompliant={product.reach_compliant}
                  />
                )}
                {product.ul_comparable && (
                  <ComplianceBadge
                    label="UL"
                    isCompliant={product.ul_comparable}
                  />
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {pagination.pages > 1 && !loadingSearch && (
        <div className="flex items-center justify-between bg-white rounded-xl px-6 py-4 border border-gray-200 shadow-sm">
          <p className="text-sm text-gray-500">
            {language === "zh"
              ? `第 ${pagination.page} 頁，共 ${pagination.pages} 頁 (${pagination.total} 個產品)`
              : `Page ${pagination.page} of ${pagination.pages} (${pagination.total} products)`}
          </p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => onPageChange?.(pagination.page - 1)}
              disabled={pagination.page <= 1 || loading}
              className="p-2 rounded-lg text-gray-500 hover:bg-orange-50 hover:text-orange-500 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>

            {Array.from({ length: Math.min(pagination.pages, 7) }, (_, i) => {
              // Show pages around the current page
              let p;
              if (pagination.pages <= 7) {
                p = i + 1;
              } else if (pagination.page <= 4) {
                p = i + 1;
              } else if (pagination.page >= pagination.pages - 3) {
                p = pagination.pages - 6 + i;
              } else {
                p = pagination.page - 3 + i;
              }
              return (
                <button
                  key={p}
                  onClick={() => onPageChange?.(p)}
                  disabled={loading}
                  className={`w-8 h-8 rounded-lg text-sm font-medium transition-all ${
                    p === pagination.page
                      ? "bg-orange-500 text-white shadow-sm"
                      : "text-gray-600 hover:bg-orange-50 hover:text-orange-500"
                  }`}
                >
                  {p}
                </button>
              );
            })}

            <button
              onClick={() => onPageChange?.(pagination.page + 1)}
              disabled={pagination.page >= pagination.pages || loading}
              className="p-2 rounded-lg text-gray-500 hover:bg-orange-50 hover:text-orange-500 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default DatabaseView;
